import { VaporToolbar } from "@vapor/react-custom";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { EllipsisVertical } from "@vapor/react-icons";
import { Button, Menu, MenuItem, Stack } from "@vapor/react-material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
    GovernmentAgencyType,
    TaxSubmissionDetailDTO,
} from "../../../../../core/api/use-get-tax-submission-detail/TaxSubmissionDetailDTO";
import { TaxSubmissionStatus } from "../../../../../core/dtos/TaxSubmissionStatus";
import { useGetCtaPreviewOutcome } from "./primary-cta/useGetCtaPreviewOutcome";
import { useGetCtaSendRejectedTaxSubmission } from "./primary-cta/useGetCtaSendRejectedTaxSubmission";
import { useGetCtaSendRevokeTaxSubmission } from "./primary-cta/useGetCtaSendRevokeTaxSubmission";
import { useGetCtaSendTaxSubmission } from "./primary-cta/useGetCtaSendTaxSubmission";
import { useGetCtaDeleteTaxSubmission } from "./secondary-cta/useGetCtaDeleteTaxSubmission";
import { useGetCtaDownloadFiles } from "./secondary-cta/useGetCtaDownloadFiles";
import { useGetCtaDownloadOriginalFile } from "./secondary-cta/useGetCtaDownloadOriginalFile";
import { useGetCtaDownloadOutcomes } from "./secondary-cta/useGetCtaDownloadOutcomes";
import { useGetCtaGoToProcedure } from "./secondary-cta/useGetCtaGoToProcedure";

export type SecondaryOption = {
    element: JSX.Element;
    loading?: boolean;
};
interface TaxSubmissionDetailToolbarProps {
    onClickPreviewOutcome: (
        availablesOutcomeTypes: string[],
        governmentAgencyType: GovernmentAgencyType,
        taxSubmissionId: string,
        selectedOutcomeType: string,
        taxPayer?: string,
    ) => unknown;
    taxPayersToRevoke: { protocol: string; taxId: string }[];
    onTaxSubmissionUpdate: () => unknown;
    revokeTaxSubmissionsEnabled: boolean;
    taxSubmissionDetail: TaxSubmissionDetailDTO;
}

export const TaxSubmissionDetailToolbar: React.FC<
    TaxSubmissionDetailToolbarProps
> = ({
    onClickPreviewOutcome,
    onTaxSubmissionUpdate,
    revokeTaxSubmissionsEnabled,
    taxPayersToRevoke,
    taxSubmissionDetail,
}): JSX.Element => {
    const { t } = useTranslation();

    const [anchorEl, setAnchorEl] = React.useState<HTMLElement>();

    const goToProcedureCta = useGetCtaGoToProcedure(taxSubmissionDetail);
    const sendRejectedCta = useGetCtaSendRejectedTaxSubmission(
        onTaxSubmissionUpdate,
        taxSubmissionDetail,
    );
    const previewOutcomeCta = useGetCtaPreviewOutcome(
        onClickPreviewOutcome,
        taxSubmissionDetail,
    );
    const downloadOutcomesCta = useGetCtaDownloadOutcomes(taxSubmissionDetail);
    const downloadOriginalFileCta = useGetCtaDownloadOriginalFile(
        taxSubmissionDetail.id,
    );
    const downloadFilesCta = useGetCtaDownloadFiles(taxSubmissionDetail.id);

    const sendRevokeTaxSubmissionCta = useGetCtaSendRevokeTaxSubmission(
        onTaxSubmissionUpdate,
        taxPayersToRevoke,
        taxSubmissionDetail,
    );
    const sendTaxSubmission = useGetCtaSendTaxSubmission(
        onTaxSubmissionUpdate,
        taxSubmissionDetail,
    );
    const deleteTaxSubmissiom = useGetCtaDeleteTaxSubmission(
        onTaxSubmissionUpdate,
        taxSubmissionDetail,
    );

    const primaryCta = useMemo(() => {
        switch (taxSubmissionDetail.status) {
            case TaxSubmissionStatus.GENERIC_ERROR:
                break;
            case TaxSubmissionStatus.READY_TO_SEND:
            case TaxSubmissionStatus.NOT_SENT_WITH_ERRORS:
                return sendTaxSubmission;
            case TaxSubmissionStatus.REJECTED:
                return sendRejectedCta;
            case TaxSubmissionStatus.PENDING:
            case TaxSubmissionStatus.SENT_WITH_WARNINGS:
            case TaxSubmissionStatus.SENT:
                return revokeTaxSubmissionsEnabled &&
                    taxSubmissionDetail.isRevocable
                    ? sendRevokeTaxSubmissionCta
                    : !revokeTaxSubmissionsEnabled
                      ? previewOutcomeCta
                      : undefined;
            default:
                break;
        }
        return undefined;
    }, [
        previewOutcomeCta,
        revokeTaxSubmissionsEnabled,
        sendRejectedCta,
        sendRevokeTaxSubmissionCta,
        sendTaxSubmission,
        taxSubmissionDetail.isRevocable,
        taxSubmissionDetail.status,
    ]);

    // altre azioni
    const secondaryCTAs = useMemo(() => {
        const options: SecondaryOption[] = [];

        // go to procedure CTA is available for each status if the urn is valid and revokeTaxSubmissionsEnabled is false
        if (!revokeTaxSubmissionsEnabled && taxSubmissionDetail) {
            options.push(goToProcedureCta);
        }

        switch (taxSubmissionDetail.status) {
            case TaxSubmissionStatus.GENERIC_ERROR:
            case TaxSubmissionStatus.PENDING:
            case TaxSubmissionStatus.REJECTED:
                break;
            case TaxSubmissionStatus.READY_TO_SEND:
                break;
            case TaxSubmissionStatus.NOT_SENT_WITH_ERRORS:
                break;
            case TaxSubmissionStatus.SENT:
                if (!revokeTaxSubmissionsEnabled)
                    options.push(...downloadOutcomesCta);

                options.push(downloadFilesCta);
                break;
            case TaxSubmissionStatus.SENT_WITH_WARNINGS:
                options.push(...downloadOutcomesCta);
                options.push(downloadFilesCta);
                break;
        }
        if (taxSubmissionDetail.isTaxSubmissionDeletable) {
            options.push(deleteTaxSubmissiom);
        }
        options.push(downloadOriginalFileCta);

        return options;
    }, [
        deleteTaxSubmissiom,
        downloadFilesCta,
        downloadOriginalFileCta,
        downloadOutcomesCta,
        goToProcedureCta,
        revokeTaxSubmissionsEnabled,
        taxSubmissionDetail,
    ]);

    return !!primaryCta || secondaryCTAs.length > 0 ? (
        <VaporToolbar
            contentRight={
                <Stack direction="row" spacing={1}>
                    {secondaryCTAs.length > 0 && (
                        <>
                            <Button
                                onClick={(event) =>
                                    setAnchorEl(event.currentTarget)
                                }
                                variant="outlined"
                            >
                                <EllipsisVertical />
                                <Typography marginLeft={2}>
                                    {t(
                                        "views.home.components.tax_submission_detail_toolbar.other_actions",
                                    )}
                                </Typography>
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={() => setAnchorEl(undefined)}
                            >
                                {secondaryCTAs.map((option, index) => (
                                    <MenuItem
                                        key={index}
                                        disabled={option.loading}
                                    >
                                        {option.element}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}

                    {primaryCta}
                </Stack>
            }
            size="large"
            variant="regular"
        />
    ) : (
        <></>
    );
};
